// App-side singleton for the 3D camera's ground footprint, written by the
// in-Canvas CameraViewPublisher and read by App to draw the minimap's view
// box. Board coordinates (Large Board's render scale already divided out).
//
// Publishing is throttled (~15 Hz) and change-only inside the publisher; this
// store is a plain last-value holder like `boardScale.ts`, not React state
// that would re-render the scene per publish.

import { create } from 'zustand';
import { polygonEquals } from './cameraFootprint.js';

export type ViewPolygon = readonly (readonly [number, number])[];

/** The camera's ground focus (the orbit target): x = world x, y = world z. */
export type ViewFocus = { readonly x: number; readonly y: number };

interface CameraViewState {
  /** Ground footprint (x, z) in NDC corner order; null before the first publish. */
  polygon: ViewPolygon | null;
  /** The ground point the camera looks at (the orbit target); null before the first publish. */
  focus: ViewFocus | null;
  publish(polygon: ViewPolygon, focus: ViewFocus | null): void;
}

/** Focus equality at board-coordinate precision (the publisher publishes change-only). */
function focusEquals(a: ViewFocus | null, b: ViewFocus | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return Math.abs(a.x - b.x) < 1e-3 && Math.abs(a.y - b.y) < 1e-3;
}

export const useCameraView = create<CameraViewState>()((set, get) => ({
  polygon: null,
  focus: null,
  publish: (polygon, focus) => {
    const state = get();
    const polygonChanged = !polygonEquals(state.polygon, polygon);
    const focusChanged = !focusEquals(state.focus, focus);
    if (!polygonChanged && !focusChanged) return;
    // Only the changed half is written, so the untouched half keeps its
    // identity and does not re-render its subscribers (a still camera must
    // not re-render App at 15 Hz).
    set({
      ...(polygonChanged ? { polygon } : {}),
      ...(focusChanged ? { focus } : {}),
    });
  },
}));
